import React, {
    ReactElement,
    useState,
} from "react";
import { Dropdown, Form } from "react-bootstrap";
import { BiCheckSquare, BiSquareRounded } from "react-icons/bi";
import { FaSlack } from "react-icons/fa";
import { GrFormFilter } from "react-icons/gr";
import {
    ChannelMode, GetSettingsDocument, IntegrationActionType,
    useGetSlackChannelSettingsQuery, useSaveSettingsMutation,
} from "../../../../../__generated__/api";
import { removeTypenameList } from "../../../../../api";
import { Loading } from "../../../../../components";
import useDirtyTracker from "../../../../../components/dirty-tracker/DirtyTracker";
import Editor from "../../../../../components/editor/Editor";
import ExpandingSection from "../../../../../components/expanding-section/ExpandingSection";
import BooleanSelect from "../../../../../components/pickers/boolean/BooleanSelect";
import IconUploader from "../../../../../components/pickers/icon-uploader/IconUploader";
import Integrations from "../../../../../components/pickers/integrations/Integrations";
import RequestTypes from "../../../../../components/pickers/request-types/RequestTypes";
import SlackBots from "../../../../../components/pickers/slack-bots/SlackBots";
import Select from "../../../../../components/select/Select";
import TagList from "../../../../../components/tag-list/TagList";
import useArticleContext from "../../../../../contexts/HelpContext";
import useRouting, { Link } from "../../../../../contexts/Routing";
import { ErrorRemoteView } from "../../../../error/Error";
import { SettingsProps } from "../../base";
import ChannelModeHelp from "./ChannelModeHelp";
import EmojiActions from "./EmojiActions";
import ChannelTagSettings from "./TagSettings";
import TicketCreateHelp from "./TicketCreateHelp";
import WelcomeMessageHelp from "./WelcomeMessageHelp";

const CHANNEL_MODES = {
    [ChannelMode.Threaded]: "Threaded",
    [ChannelMode.SingleThread]: "One ticket at a time",
    [ChannelMode.Manual]: "Manual",
};

export default function SlackSettings(props: SettingsProps): ReactElement {
    useArticleContext("urn:fq:article:8769af37db0c44c887cc527d2a885100");

    const [save] = useSaveSettingsMutation();
    const { Paths } = useRouting();
    const { data: chatSettings, loading: isChatLoading, error: chatError } = useGetSlackChannelSettingsQuery({
        fetchPolicy: "no-cache",
    });

    const [search, setSearch] = useState("");
    const [tags, setTags] = useState<Set<string>>(new Set());

    const [introduction, setIntroduction] = useState(props.settings?.channelSlack?.introduction?.rawText ?? "<p></p>");
    const [offHoursIntroduction, setOffHoursIntroduction] = useState(
        props.settings?.channelSlack?.offHoursIntroduction?.rawText ?? "<p></p>",
    );
    const [appName, setAppName] = useState(props.settings?.channelSlack?.name);
    const [appIcon, setAppIcon] = useState(props.settings?.channelSlack?.iconUrl);
    const [channelMode, setChannelMode] = useState(props.settings?.channelSlack?.channelMode ?? ChannelMode.Threaded);
    const [formIds, setFormIds] = useState(props.settings?.channelSlack?.formIds);
    const [messageActionFormIds, setMessageActionFormIds] = useState(props.settings?.channelSlack?.messageActionFormIds);
    const [approvedBotIds, setApprovedBotIds] = useState(props.settings?.channelSlack?.approvedBotIds);
    const [requestFormText, setRequestFormText] = useState(props.settings?.channelSlack?.requestFormText);
    const [ticketCreatedTemplate, setTicketCreatedTemplate] = useState(props.settings?.channelSlack?.ticketCreatedTemplate);
    const [emojiActions, setEmojiActions] = useState(props.settings?.channelSlack?.emojiActions ?? []);

    const [identities, setIdentities] = useState<string[]>(() => props.settings?.identities ?? []);
    const [shouldAllowUsersAddParticipants, setShouldAllowUsersAddParticipants] = useState(
        props.settings?.channelSlack?.shouldAllowUsersAddParticipants,
    );

    const [slaTierId, setSlaTierId] = useState(props.settings?.channelSlack?.slaTierId);
    const [canFillOutForm, setCanFillOutForm] = useState(props.settings?.channelSlack?.canFillOutForm ?? true);

    const [ignoreTicketsFromEmailDomains, setIgnoreTicketsFromEmailDomains] = useState(
        props.settings?.channelSlack?.ignoreTicketsFromEmailDomains,
    );
    const [shouldDisplayAgentJoin, setShouldDisplayAgentJoin] = useState(
        props.settings?.channelSlack?.shouldDisplayAgentJoin ?? false,
    );

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Slack settings Saved",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        identities,
                        channelSlack: {
                            name: appName,
                            iconUrl: appIcon,
                            introduction: {
                                rawText: introduction,
                            },
                            offHoursIntroduction: {
                                rawText: offHoursIntroduction,
                            },

                            shouldDisplayAgentJoin,
                            ignoreTicketsFromEmailDomains,
                            channelMode,
                            formIds,
                            messageActionFormIds,
                            approvedBotIds,
                            requestFormText,
                            shouldAllowUsersAddParticipants,
                            slaTierId,
                            ticketCreatedTemplate,
                            emojiActions: removeTypenameList(emojiActions),
                            canFillOutForm,

                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchProperty(introduction, props.settings?.channelSlack?.introduction?.rawText ?? "<p></p>");
    dirtyTracker.useWatchProperty(offHoursIntroduction, props.settings?.channelSlack?.offHoursIntroduction?.rawText ?? "<p></p>");
    dirtyTracker.useWatchProperty(appName, props.settings?.channelSlack?.name);
    dirtyTracker.useWatchProperty(appIcon, props.settings?.channelSlack?.iconUrl);
    dirtyTracker.useWatchProperty(ignoreTicketsFromEmailDomains, props.settings?.channelSlack?.ignoreTicketsFromEmailDomains);
    dirtyTracker.useWatchProperty(shouldDisplayAgentJoin, props.settings?.channelSlack?.shouldDisplayAgentJoin ?? false);
    dirtyTracker.useWatchProperty(channelMode, props.settings?.channelSlack?.channelMode ?? ChannelMode.Threaded);
    dirtyTracker.useWatchProperty(formIds, props.settings?.channelSlack?.formIds);
    dirtyTracker.useWatchProperty(messageActionFormIds, props.settings?.channelSlack?.messageActionFormIds);
    dirtyTracker.useWatchProperty(approvedBotIds, props.settings?.channelSlack?.approvedBotIds);
    dirtyTracker.useWatchProperty(
        shouldAllowUsersAddParticipants,
        props.settings?.channelSlack?.shouldAllowUsersAddParticipants ?? false,
    );
    dirtyTracker.useWatchProperty(slaTierId, props.settings?.channelSlack?.slaTierId);
    dirtyTracker.useWatchProperty(ticketCreatedTemplate, props.settings?.channelSlack?.ticketCreatedTemplate);
    dirtyTracker.useWatchProperty(JSON.stringify(emojiActions), JSON.stringify(props.settings?.channelSlack?.emojiActions ?? []));
    dirtyTracker.useWatchProperty(identities, props.settings?.identities ?? []);
    dirtyTracker.useWatchProperty(requestFormText, props.settings?.channelSlack?.requestFormText);
    dirtyTracker.useWatchProperty(canFillOutForm ?? true, props.settings?.channelSlack?.canFillOutForm ?? true);

    dirtyTracker.useLoad(() => {
        setIntroduction(props.settings?.channelSlack?.introduction?.rawText ?? "<p></p>");
        setOffHoursIntroduction(props.settings?.channelSlack?.offHoursIntroduction?.rawText ?? "<p></p>");
        setAppName(props.settings?.channelSlack?.name);
        setAppIcon(props.settings?.channelSlack?.iconUrl);
        setIgnoreTicketsFromEmailDomains(props.settings?.channelSlack?.ignoreTicketsFromEmailDomains);
        setShouldDisplayAgentJoin(props.settings?.channelSlack?.shouldDisplayAgentJoin ?? false);
        setChannelMode(props.settings?.channelSlack?.channelMode ?? ChannelMode.Threaded);
        setFormIds(props.settings?.channelSlack?.formIds);
        setMessageActionFormIds(props.settings?.channelSlack?.messageActionFormIds);
        setApprovedBotIds(props.settings?.channelSlack?.approvedBotIds);
        setShouldAllowUsersAddParticipants(props.settings?.channelSlack?.shouldAllowUsersAddParticipants ?? false);
        setSlaTierId(props.settings?.channelSlack?.slaTierId);
        setTicketCreatedTemplate(props.settings?.channelSlack?.ticketCreatedTemplate);
        setEmojiActions(props.settings?.channelSlack?.emojiActions ?? []);

        setIdentities(props.settings?.identities ?? []);

        setRequestFormText(props.settings?.channelSlack?.requestFormText);

        setCanFillOutForm(props.settings?.channelSlack?.canFillOutForm ?? true);
    }, [props.settings]);

    let channels = chatSettings?.chatSettingsMany?.sortBy(
        row => row.options?.slack?.workspaceName?.toLowerCase() ?? "",
        row => row.options?.slack?.name?.toLowerCase() ?? "",
    ) ?? [];

    const firstWorkspaceName = channels.firstElement?.options?.slack?.workspaceName;
    const shouldDisplayWorkspaceName = channels.some(row => row.options?.slack?.workspaceName !== firstWorkspaceName);

    if (tags.size > 0) {
        channels = channels.filter(channel => channel.options?.slack?.tags?.some(tag => tags.has(tag)));
    }

    if (search) {
        const searchText = search.toLowerCase();
        channels = channels.filter(channel => channel.options?.slack?.name?.toLowerCase().includes(searchText)
             || (shouldDisplayWorkspaceName && channel.options?.slack?.workspaceName?.toLowerCase().includes(searchText))
             || channel.options?.slack?.tags?.some(tag => tag.toLowerCase().includes(searchText)));
    }

    const currentSlaTier = slaTierId ? props.settings?.sla?.tiers?.find(tier => tier.id === slaTierId) : undefined;

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    Brand
                </h5>

                <Form.Group>
                    <Form.Label className="fw-bolder d-block mb-0">Application Icon</Form.Label>
                    <Form.Text className="mb-2 d-block">
                        The default icon the bot will use. This usually appears in automated responses. Max image size is 20 MBs
                    </Form.Text>
                    <IconUploader value={appIcon as string} onChange={icon => setAppIcon(icon)} shouldStore />
                </Form.Group>

                <Form.Group className="mt-4">

                    <Form.Label className="fw-bolder">Application Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Agent bot"
                        onChange={evt => setAppName(evt.target.value)}
                        value={appName as string}
                    />
                    <Form.Text>
                        The default application name the bot will use. This usually appears for automated responses
                    </Form.Text>
                </Form.Group>
            </div>
            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    Welcome Messages
                </h5>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">
                        Introduction Message
                        {" "}
                        <WelcomeMessageHelp />
                    </Form.Label>
                    <div className="bg-white border-light border rounded">
                        <Editor
                            text={introduction as string}
                            onChange={data => setIntroduction(data)}
                            shouldFocus={false}
                            placeholder="Hey, remember to use a thread..."
                        />
                    </div>
                    <Form.Text>
                        When a customer first asks a question, this is the message they will first see.
                        {" "}
                        <WelcomeMessageHelp />
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">
                        Off-hours Introduction message
                        {" "}
                        <WelcomeMessageHelp />
                    </Form.Label>
                    <div className="bg-white border-light border rounded">
                        <Editor
                            text={offHoursIntroduction as string}
                            shouldFocus={false}
                            onChange={data => setOffHoursIntroduction(data)}
                            placeholder="Sorry, we are currently not online. If this is an emergency..."
                        />
                    </div>
                    <Form.Text>
                        Off Hours are controlled by hours of operation settings below. If no message is
                        specified, Agent will display the default introduction message.
                        {" "}
                        <WelcomeMessageHelp />
                    </Form.Text>
                </Form.Group>

            </div>

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    Channel Defaults
                </h5>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">
                        Channel Mode
                        {" "}
                        <ChannelModeHelp />
                    </Form.Label>

                    <Select
                        className="text-gray"
                        placeholder=""
                        onChange={value => setChannelMode(value.value as ChannelMode)}
                        options={Object.entries(CHANNEL_MODES).map(([value, label]) => ({ value, label }))}
                        value={!channelMode ? undefined : {
                            value: channelMode,
                            label: CHANNEL_MODES[channelMode],
                        }}
                    />
                    <Form.Text>
                        Threaded means tickets are associated with each thread.
                        One ticket at a time means tickets are associated with the entire channel.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Request Types</Form.Label>
                    <RequestTypes
                        isMulti
                        placeholder="Which request types should be default"
                        onSelected={selected => setFormIds(selected)}
                        value={formIds as string[]}
                    />
                    <Form.Text>
                        When a user asks a question, Agent will display a set of request types for the user to fill out.
                        To create a new request type or edit existing ones, view
                        {" "}
                        <Link to={Paths.ADMINISTRATOR_SETTING} params={{ setting: "request-types" }}>Request types</Link>
                        . To order the request types, click and drag them inside this box.
                    </Form.Text>
                </Form.Group>

                <ExpandingSection title="Advanced" classBackground="bg-gray-100">

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Ticket Integration</Form.Label>
                        <Integrations
                            placeholder="Which integration should create a ticket"
                            onSelected={selected => {
                                setIdentities(selected ? [selected] : []);
                            }}
                            canSetNone
                            value={identities.find(identity => identity.startsWith("urn:fq:integration"))}
                            actions={[IntegrationActionType.CreateTicket]}
                        />
                        <Form.Text>
                            When creating a new ticket, Agent can automatically create a ticket in a 3rd party
                            services such as Zendesk, Intercom, Atlassian, or something else. Which existing
                            integration would you like to use for this?

                        </Form.Text>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label className="fw-bolder">Show join/leave notifications</Form.Label>
                        <BooleanSelect
                            className="text-gray"
                            isRequired
                            value={shouldDisplayAgentJoin as boolean}
                            onChange={(value: boolean) => setShouldDisplayAgentJoin(value)}
                        />
                        <Form.Text>
                            Should the Agent app display a message when an agent joins a conversation or not do anything?
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Message Action Request Types</Form.Label>
                        <RequestTypes
                            isMulti
                            placeholder="Which request types should be default"
                            onSelected={selected => setMessageActionFormIds(selected)}
                            value={messageActionFormIds as string[]}
                        />
                        <Form.Text>
                            This setting controls which request types are able to be created by users
                            when you hover over any message and select Create Ticket.
                            {" "}
                            <Link to={Paths.ADMINISTRATOR_SETTING} params={{ setting: "request-types" }}>Request types</Link>
                            . To order the request types, click and drag them inside this box.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">Request form Text</Form.Label>
                        <div className="bg-white border-light border rounded">
                            <Editor
                                text={requestFormText as string}
                                onChange={data => setRequestFormText(data)}
                                shouldFocus={false}
                            />
                        </div>
                        <Form.Text>
                            When showing a request type form to fill out, these are the instructions that are displayed.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">
                            Ticket Created Message Template
                            {" "}
                            <TicketCreateHelp />
                        </Form.Label>
                        <div className="bg-white border-light border rounded">
                            <Editor
                                text={ticketCreatedTemplate as string}
                                onChange={data => setTicketCreatedTemplate(data)}
                                placeholder="New request created: {{SUBJECT}}"
                                shouldFocus={false}
                            />
                        </div>
                        <Form.Text>
                            When a ticket is created and a new thread is posted in the channel,
                            what information should this ticket contain?
                            <TicketCreateHelp buttonClassName="m-0 ms-1" />
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">
                            Fill out form prompt
                        </Form.Label>
                        <div className="bg-white border-light border rounded">
                            <BooleanSelect
                                value={canFillOutForm as boolean}
                                onChange={value => setCanFillOutForm(value ?? true)}
                            />
                        </div>
                        <Form.Text>
                            When creating a ticket, if a request type is specified in a channel, the will see a
                            &quot;Fill out form&quot;
                            button to provide more context and fill out the request type. However, in the case of
                            a channel menu, you may not want that option. By setting this to No, users will not see
                            the option to fill out the request.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">
                            Approved Bots
                        </Form.Label>
                        <SlackBots
                            isMulti
                            placeholder="Which bot messages should not be ignored."
                            value={approvedBotIds as string[]}
                            onSelected={selected => setApprovedBotIds(selected)}
                        />
                        <Form.Text>
                            By default, Agent always blocks messages from other bots in Slack. However, you can
                            override this behavior and have Agent injest messages and create tickets from other
                            bots in your Slack workspace
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">Allow users to add participants</Form.Label>
                        <div className="bg-white border-light border rounded">
                            <BooleanSelect
                                isRequired
                                value={shouldAllowUsersAddParticipants as boolean}
                                onChange={(value: boolean) => setShouldAllowUsersAddParticipants(value)}
                            />
                        </div>
                        <Form.Text>
                            If allowed, users will be able to @mention other users and add them as participants to the
                            conversation.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Ignore Internal Messages</Form.Label>
                        <TagList
                            placeholder="@example.com"
                            value={ignoreTicketsFromEmailDomains as string[] ?? []}
                            onChange={value => setIgnoreTicketsFromEmailDomains(value)}
                            canNotHaveDuplicates
                        />
                        <Form.Text>
                            Lets say your marketing team is always sending promotional content in
                            customer channels. You don&apos;t want a ticket created for each one. By adding
                            their email address or entire email domain (such as @example.com) into this
                            box, we will not create a ticket for any message that match a users email or email domain.
                            This setting overrides the same setting in the general configuration.
                            If any domains or emails are present in this list, we will ignore the general setting.
                        </Form.Text>
                    </Form.Group>

                    {props.settings?.sla?.tiers?.isNotEmpty && (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">SLA Tier</Form.Label>
                            <Select
                                options={props.settings?.sla?.tiers?.map(tier => ({
                                    label: tier.name as string,
                                    value: tier.id as string,
                                    data: tier,
                                })) ?? []}
                                isClearable
                                onChange={tier => setSlaTierId(tier?.data?.id)}
                                value={!currentSlaTier ? undefined : {
                                    label: currentSlaTier.name as string,
                                    value: currentSlaTier.id as string,
                                    data: currentSlaTier,
                                }}
                            />
                            <Form.Text>
                                Which SLA Tier should be applied by default.
                            </Form.Text>
                        </Form.Group>
                    )}
                </ExpandingSection>
            </div>

            <EmojiActions actions={emojiActions} onChange={setEmojiActions} />

            <ChannelTagSettings />

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    Channel Settings
                </h5>
                {isChatLoading && <Loading />}
                {chatError && <ErrorRemoteView error={chatError} />}
                {chatSettings?.chatSettingsMany?.isEmpty && (
                    <div className="text-light text-center my-4">
                        Agent is not installed in any channels in Slack. Please use the
                        {" "}
                        <code>/invite @Agent</code>
                        {" "}
                        slash command in Slack to invite Agent into a channel.
                    </div>
                )}
                {chatSettings?.chatSettingsMany?.isNotEmpty && (
                    <div>
                        <div className="d-flex mb-1">
                            <Dropdown autoClose="outside">
                                <Dropdown.Toggle
                                    variant=""
                                    className="menu-small p-1"
                                >
                                    <GrFormFilter />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {chatSettings
                                        .chatSettingsMany
                                        .flatMap(setting => setting.options?.slack?.tags ?? [])
                                        .dedupe()
                                        .map(tag => (
                                            <Dropdown.Item
                                                key={tag}
                                                eventKey={tag}
                                                onClick={() => {
                                                    if (tags.has(tag)) {
                                                        tags.delete(tag);
                                                    } else {
                                                        tags.add(tag);
                                                    }
                                                    setTags(new Set(Array.from(tags)));
                                                }}
                                            >
                                                {tags.has(tag) ? <BiCheckSquare /> : <BiSquareRounded />}
                                                {" "}
                                                {tag}
                                            </Dropdown.Item>
                                        ))}
                                </Dropdown.Menu>
                            </Dropdown>
                            <input
                                className="no-border ms-2"
                                type="search"
                                placeholder="Search..."
                                value={search}
                                onChange={evt => setSearch(evt.target.value)}
                            />
                        </div>
                        {channels.isEmpty && (search || tags.size > 0) && (
                            <div className="text-light text-center">
                                No Channels found matching your filters
                            </div>
                        )}
                        {channels.isNotEmpty && (
                            <div className="list-group">

                                {channels?.map(setting => (
                                    <Link
                                        key={setting.id}
                                        to={Paths.ADMINISTRATOR_SETTING_ID}
                                        params={{
                                            id: setting.id,
                                            setting: "chat",
                                        }}
                                        className={"list-group-item list-group-item-action d-flex "
                                                + "align-items-center justify-content-between"}
                                    >
                                        <div className="d-flex align-items-center">

                                            <FaSlack />
                                            {shouldDisplayWorkspaceName && (
                                                <small className="ms-2 badge bg-light text-white">
                                                    {setting.options?.slack?.workspaceName}
                                                </small>
                                            )}
                                            <h5 className="mb-0 d-inline ms-2">
                                                {setting.options?.slack?.name}
                                            </h5>

                                        </div>

                                        <small className="ms-3 text-light">
                                            {setting.options?.slack?.tags?.dedupe().map(tag => (
                                                <span
                                                    key={`${setting.id}:${tag}`}
                                                    className="me-2 badge rounded-pill bg-blue-dark"
                                                >
                                                    #
                                                    {tag.toLowerCase()}
                                                </span>
                                            ))}
                                        </small>
                                    </Link>

                                ))}
                            </div>
                        )}

                    </div>
                )}

            </div>
            {dirtyTracker.saveComponent}
        </form>
    );
}
